import {
  DetailedRequisitionSerializer,
  RequisitionRow,
} from "../../recoil/types";
import { GRID_REPORT_ID_PLACEHOLDER } from "../../recoil/helpers";
import { CfcPatientData } from "./types";

export const PROVIDER_GRID_ROW_HEIGHT = 70;
export const CFC_BANNER_HEIGHT = 38;
export const UNITY_CONFIRM_START_DATE = "2026-05-28";

const parseDate = (dateStr?: string | null): Date | null => {
  if (!dateStr) return null;

  const parsedDate = new Date(`${dateStr}T12:00:00.000Z`);
  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  return parsedDate;
};

export const formatCfcCollectByDate = (dateStr?: string | null): string => {
  const collectByDate = parseDate(dateStr);
  if (!collectByDate) return "";

  return collectByDate.toLocaleDateString("en-US", {
    month: "2-digit",
    day: "2-digit",
    year: "numeric",
  });
};

export const formatCfcCollectByDateLong = (dateStr?: string | null): string => {
  const collectByDate = parseDate(dateStr);
  if (!collectByDate) return "";

  return collectByDate.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export const isCollectByDateInFuture = (dateStr?: string | null): boolean => {
  const collectByDate = parseDate(dateStr);
  if (!collectByDate) return false;

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return collectByDate >= today;
};

export const isReportDateOnOrAfterUnityConfirmStartDate = (
  dateStr?: string | null,
): boolean => {
  const reportDate = parseDate(dateStr);
  if (!reportDate) return false;

  const unityConfirmStartDate = parseDate(UNITY_CONFIRM_START_DATE);
  return !!unityConfirmStartDate && reportDate >= unityConfirmStartDate;
};

export const isCfcStatusOrderable = (cfcStatus?: string | null): boolean =>
  !cfcStatus || cfcStatus === "Not Ordered";

type CfcEligibilityOptions = {
  requireConditions?: boolean;
  orderSubmitted?: boolean;
};

const isRequisitionRow = (
  source: RequisitionRow | DetailedRequisitionSerializer,
): source is RequisitionRow => "reqID" in source;

export const isEligibleForCfc = (
  source: RequisitionRow | DetailedRequisitionSerializer,
  {
    requireConditions = false,
    orderSubmitted = false,
  }: CfcEligibilityOptions = {},
): boolean => {
  const isCfcEligibilityOverridden = isRequisitionRow(source)
    ? source.isCfcEligibilityOverridden
    : source.is_cfc_eligibility_overridden;
  const cfcStatus = isRequisitionRow(source)
    ? source.cfcStatus
    : source.cfc_report.status;
  const cfcEligibleCollectByDate = isRequisitionRow(source)
    ? source.cfcEligibleCollectByDate
    : source.aneuploidy_report.cfc_eligible_collect_by_date;
  const aneuploidyReportDate = isRequisitionRow(source)
    ? source.aneuploidyReportDate
    : source.aneuploidy_report.report_date;
  const cfcEligibleConditionsCount = isRequisitionRow(source)
    ? source.cfcEligibleConditions.length
    : source.aneuploidy_report.cfc_eligible_conditions.length;

  if (orderSubmitted || !isCfcStatusOrderable(cfcStatus)) {
    return false;
  }
  if (isCfcEligibilityOverridden) {
    return true;
  }
  if (requireConditions && cfcEligibleConditionsCount === 0) {
    return false;
  }
  return (
    isCollectByDateInFuture(cfcEligibleCollectByDate) &&
    isReportDateOnOrAfterUnityConfirmStartDate(aneuploidyReportDate)
  );
};

export const buildCfcPatientDataFromRow = (
  row: RequisitionRow,
): CfcPatientData => ({
  reqId: row.reqID,
  sfId: row.sfID,
  firstName: row.name.split(" ")[0] || "",
  lastName: row.name.split(" ").slice(1).join(" ") || "",
  dateOfBirth: row.dob,
  aneuploidyReportId:
    row.aneuploidyReportID === GRID_REPORT_ID_PLACEHOLDER
      ? ""
      : row.aneuploidyReportID,
  cfcEligibleCollectByDate: row.cfcEligibleCollectByDate || "",
  eligibleConditions: row.cfcEligibleConditions,
  isCfcEligibilityOverridden: row.isCfcEligibilityOverridden,
});

export const buildCfcPatientDataFromDetailedRequisition = (
  requisition: DetailedRequisitionSerializer,
): CfcPatientData => ({
  reqId: requisition.req_identifier || "",
  sfId: requisition.sfid || "",
  firstName: requisition.first_name || "",
  lastName: requisition.last_name || "",
  dateOfBirth: requisition.date_of_birth || "",
  aneuploidyReportId: requisition.aneuploidy_report.report_pk || "",
  cfcEligibleCollectByDate:
    requisition.aneuploidy_report.cfc_eligible_collect_by_date || "",
  eligibleConditions: requisition.aneuploidy_report.cfc_eligible_conditions,
  isCfcEligibilityOverridden: requisition.is_cfc_eligibility_overridden,
});
